import type { Animal } from "../animals";
import { nameStep } from "../handwriting";

// An ORIGINAL kawaii snail (Easy / level 5) — our own linework, not a trace of
// any tutorial. Classic snail shape: a big round spiral shell on the right, a
// small happy head on the left facing forward, two feelers with round-loop
// tips, and a wavy foot with a little upturned tail. The body's top edge is an
// OPEN stroke (no line under the shell); a closed body FILL sits behind the
// opaque shell fill so nothing dark crosses the shell.

const OUTLINE = "#5b4a3f";
const BODY = "#c3e0a0";
const SHELL = "#f4a259";
const CHEEK = "#ff9db0";

const ell = (cx: number, cy: number, rx: number, ry: number) =>
  `M ${cx - rx},${cy} a ${rx},${ry} 0 1,0 ${2 * rx},0 a ${rx},${ry} 0 1,0 ${-2 * rx},0`;

// Shell coil (outer → inner) as a polyline.
function spiral(cx: number, cy: number, rMax: number, turns: number, steps = 140) {
  const pts: string[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const r = rMax * (1 - t) + 2 * t;
    const a = turns * 2 * Math.PI * t + Math.PI * 0.5; // start toward the bottom
    pts.push(`${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`);
  }
  return "M " + pts.join(" L ");
}

// --- body (head + foot). Open across the top where the shell sits. ---------
const BODY_OPEN =
  "M 46,76 Q 28,82 26,102 Q 25,118 40,124 Q 38,138 44,150 " +
  "Q 47,156 58,156 L 150,156 Q 168,156 168,146";
const BODY_FILL = BODY_OPEN + " Q 150,72 100,66 Q 70,62 46,76 Z";

// --- shell -----------------------------------------------------------------
const SHELL_C = ell(124, 90, 52, 52);
const SPIRAL = spiral(124, 90, 45, 2.7);

// --- feelers (stalks + round loop tips) ------------------------------------
const ANT_L = "M 46,80 Q 37,56 40,43";
const ANT_R = "M 58,82 Q 68,56 68,41";
const TIP_L = ell(39, 39, 5, 5);
const TIP_R = ell(69, 37, 5, 5);

// --- face (on the head, left) ----------------------------------------------
const EYE_L = "M 34,90 Q 37,95 40,90"; // happy closed eyes (little U arcs)
const EYE_R = "M 48,90 Q 51,95 54,90";
const MOUTH = "M 38,101 Q 44,107 50,101";
const CHEEK_L = ell(33, 100, 4, 2.8);

export const snail: Animal = {
  id: "snail",
  name: "Snail",
  emoji: "🐌",
  viewBox: "0 0 200 200",
  color: OUTLINE,
  level: 5,
  steps: [
    { hint: "Draw the snail's long foot 🐌", strokes: [BODY_OPEN] },
    { hint: "Add the big round shell 🐚", strokes: [SHELL_C] },
    { hint: "Draw the swirl on the shell 🌀", strokes: [SPIRAL] },
    { hint: "Add two little feelers 👀", strokes: [ANT_L, TIP_L, ANT_R, TIP_R] },
    { hint: "Draw the happy eyes 👀", strokes: [EYE_L, EYE_R] },
    { hint: "Add a rosy cheek and a smile 😊", strokes: [CHEEK_L, MOUTH] },
    {
      hint: "Now colour your snail in! 🖍️",
      strokes: [],
      fills: [
        { d: BODY_FILL, color: BODY },
        { d: SHELL_C, color: SHELL },
        { d: CHEEK_L, color: CHEEK },
      ],
    },
    nameStep("SNAIL", { baseline: 194, height: 16 }),
  ],
};
